import type { ParallaxSvgSpec } from '../../../../config/atmosphere/effectLayer.types';
import { placeWorldShape, type ShapePlacement } from './worldShapeKit';
import { ELEMENTAL_DEPTH_BACKDROPS, ELEMENTAL_DEPTH_HEIGHTS } from './elementalDepthBackdrops';
import { FOREST_DEPTH_BACKDROPS, FOREST_DEPTH_HEIGHTS } from './forestDepthBackdrops';
import { REGIONAL_DEPTH_BACKDROPS, REGIONAL_DEPTH_HEIGHTS } from './regionalDepthBackdrops';

/** Existing mineral, ruin and geology shapes recomposed at deliberate widths.
 * Matching display/viewBox height avoids scaling a short wall into a curtain. */
function depthTile(height: number, shapes: readonly ShapePlacement[], bank = ''): ParallaxSvgSpec {
  return { tile: { viewBoxWidth: 2400, viewBoxHeight: height,
    path: [bank, ...shapes.map(shape => placeWorldShape(...shape))].filter(Boolean).join(' ') } };
}

export const CONNECTED_DEPTH_HEIGHTS = {
  crystal_caverns: 1500, ancient_temple: 1400, sunken_ruins: 1280,
  frozen_lake: 900, volcano_summit: 1320,
  stormbreak_cliffs: 1320, sunless_deep: 1200,
  ...ELEMENTAL_DEPTH_HEIGHTS,
  dark_forest: FOREST_DEPTH_HEIGHTS.deep_woods,
  ...REGIONAL_DEPTH_HEIGHTS,
} as const;

export const CONNECTED_DEPTH_BACKDROPS = {
  crystal_caverns: depthTile(1500, [
    ['crystal', 120, 1500, 380, 1060], ['crystal', 1120, 1500, 230, 870],
    ['crystal', 1840, 1500, 340, 990], ['basalt', 690, 1500, 260, 260],
  ]),
  ancient_temple: depthTile(1400, [
    ['column', 220, 1400, 70, 1180], ['ruin', 650, 1400, 360, 1080],
    ['column', 1450, 1400, 80, 1280], ['ruin', 1800, 1400, 380, 1000],
  ]),
  sunken_ruins: depthTile(1280, [
    ['ruin', 100, 1280, 460, 1000], ['column', 900, 1280, 80, 840],
    ['ruin', 1320, 1280, 520, 1050],
  ], 'M0 1280V1180Q200 1180 430 1168L800 1168Q1030 1190 1240 1180'
    + 'L1540 1180Q1770 1160 2010 1172Q2240 1180 2400 1180V1280Z'),
  frozen_lake: depthTile(900, [
    ['mountain', 120, 900, 700, 650], ['crystal', 1050, 900, 130, 240],
    ['mountain', 1400, 900, 650, 560],
  ], 'M0 900V820L300 812L620 824L950 814L1280 826L1620 812L2000 818L2400 820V900Z'),
  volcano_summit: depthTile(1320, [
    ['basalt', 140, 1320, 330, 1040], ['volcano', 750, 1320, 620, 980],
    ['basalt', 1720, 1320, 400, 1120],
  ], 'M0 1320V1220L260 1208L510 1228L900 1216L1260 1224L1610 1204L2010 1228L2400 1220V1320Z'),
  // Separated wind-cut cliffs reach the sea-stack route while leaving the
  // storm sky open. The thin eastern stack breaks the two larger masses.
  stormbreak_cliffs: depthTile(1320, [
    ['mesa', 140, 1320, 460, 1160], ['basalt', 1420, 1320, 380, 1040],
    ['obelisk', 2140, 1320, 95, 990],
  ], 'M0 1320V1230L270 1220L600 1240L950 1228L1310 1242L1700 1216L2100 1234L2400 1230V1320Z'),
  // A dry chasm: a split stone rib, broken crag and narrow spire, with no
  // enclosing ceiling or continuous wall that would swallow the distant void.
  sunless_deep: depthTile(1200, [
    ['ribs', 140, 1200, 520, 920], ['basalt', 1050, 1200, 330, 890],
    ['obelisk', 1830, 1200, 110, 1020],
  ], 'M0 1200V1140L280 1130L620 1146L980 1138L1300 1148L1640 1130L2030 1144L2400 1140V1200Z'),
  ...ELEMENTAL_DEPTH_BACKDROPS,
  dark_forest: FOREST_DEPTH_BACKDROPS.deep_woods,
  ...REGIONAL_DEPTH_BACKDROPS,
} satisfies Record<keyof typeof CONNECTED_DEPTH_HEIGHTS, ParallaxSvgSpec>;
